/**
 * Projection — the ONE integer→float and float→integer boundary in the
 * client.
 *
 * See `wp6-client-render.md` § "Float boundary (doctrine)" and
 * § "Projection (normative)". The doctrine is short:
 *
 *   • The sim is integers. Presentation is floats. That is correct.
 *   • Conversion happens in EXACTLY TWO functions: `worldToScreen` and
 *     `screenToWorld`. No other module converts.
 *   • Interpolation outputs `RenderWorldPos` — a distinct type so that
 *     float world-coords can never leak into protocol types.
 *
 * ── The basis is AXIS-ALIGNED (#730) ─────────────────────────────────────────
 * The transform is a tilted-overhead camera projected onto the world's OWN
 * axes, with the depth axis foreshortened by {@link ISO_Y_COMPRESSION} (`k`):
 *
 *     sx = x * ISO_SCALE_X * zoom + centerX
 *     sy = y * ISO_SCALE_Y * zoom + centerY        (ISO_SCALE_Y = k·ISO_SCALE_X)
 *
 * World +x is screen right; world +y is screen down, squashed by `k`. A
 * world-space RECTANGLE therefore lands as a screen-axis-aligned RECTANGLE.
 *
 * It used to be a ROTATED basis (`sx = (x−y)·SX`, `sy = (x+y)·SY`) — a 45°
 * rotation, which projects the rectangular map to a DIAMOND. A diamond can
 * never fill a rectangular viewport: stand in a map corner and you are inside
 * a ~37° wedge, so the rest of the screen is unplayable black no matter how
 * the camera zooms or how the map is dimensioned (the diamond's aspect ratio
 * is the constant `ISO_SCALE_X / ISO_SCALE_Y` for EVERY map — see #553, which
 * proved dimensions cannot fix the silhouette). #730's requirement is "every
 * pixel of the viewport shows playable area, from every camera position", and
 * axis-aligning the basis is the only change that satisfies it. Map edge now
 * coincides with screen edge everywhere, corners included.
 *
 * The vertical squash is KEPT (the view still reads as a tilted overhead
 * camera, not flat top-down); `k` was unchanged by #730 and is `0.650647`
 * (`= sin(40.59°)`, the camera elevation) since #842 — see
 * {@link ISO_Y_COMPRESSION} for why the anisotropy #338 complained about is
 * NOT reintroduced by the rotation change.
 *
 * ── `ProjectionCenter` is the full viewport transform ────────────────────────
 * A `ProjectionCenter` is `{ cx, cy, zoom }`: a uniform scale about the world
 * origin followed by a translation. {@link NO_OFFSET} is the identity
 * (`zoom = 1`, no translation) and is ALSO the coordinate space Pixi world
 * layers are drawn in — `mount.ts` applies the live viewport transform by
 * scaling + positioning those containers, so draw code keeps passing
 * `NO_OFFSET` and only true screen-space consumers (hit-test, cursor→world,
 * DOM HUD overlays, the Three camera) pass the live centre.
 *
 * @pit/protocol and @pit/sim are imported for TYPES ONLY (branded ints,
 * facing sector count). This is enforced by the render-scope lint rule.
 */

import type { Units } from "@pit/protocol";

/**
 * BASE-SCALE factor only — NOT the framing knob (#782).
 *
 * It is baked into {@link ISO_SCALE_X} (`BASE_ISO_SCALE_X * DEFAULT_ZOOM`),
 * so it is load-bearing for `SX`/`SY` and therefore for every projected
 * quantity derived from them (`projectRadius`, {@link ISO_VERTICAL_PX_PER_UNIT},
 * `three3d/isoCamera`, every art constant sized against them). Changing its
 * numeric value silently rescales ALL of those — do not treat it as a "how
 * zoomed in is the camera" dial.
 *
 * That dial is now {@link TARGET_WORLD_H}: since #782 the follow camera's zoom
 * is `max(zero-black floor, zTarget)` (see {@link viewportFollowZoom}), and
 * `zTarget` cancels this constant out entirely — `zTarget · ISO_SCALE_Y`
 * depends only on `BASE_ISO_SCALE_X · ISO_Y_COMPRESSION`. Pre-#782 this WAS
 * the framing knob in name only: #778's pure cover zoom sat exactly on the
 * zero-black floor at every real viewport, so the value never actually
 * decided framing.
 *
 * History: 4×, then +20% to 4.8× (#625); 5.6× per Joseph's request; 6.2×
 * (#753). Pure client-presentation constant — no sim/wire coupling, no
 * `SIM_VERSION`/golden regen implication.
 */
export const DEFAULT_ZOOM = 6.2;

/** Base world→pixel scale along the screen X (right) axis at zoom 1
 *  (the historical 0.04). Actual scale = base × DEFAULT_ZOOM × viewport zoom. */
const BASE_ISO_SCALE_X = 0.04;

/** Screen pixels per world unit along the screen X (right) axis, at viewport
 *  zoom 1. World +x maps straight to screen right (#730). */
export const ISO_SCALE_X = BASE_ISO_SCALE_X * DEFAULT_ZOOM;

/**
 * Depth-compression ratio `k = ISO_SCALE_Y / ISO_SCALE_X` — how much the
 * world's depth axis (+y, screen down) is foreshortened by the camera's tilt.
 * This is what keeps the view reading as a tilted overhead camera instead of
 * flat top-down.
 *
 * THIS CONSTANT IS THE CAMERA ELEVATION, expressed as a ratio: `k = sin(EL)`,
 * i.e. `elevation = asin(k)`. The 3D layer (`three3d/isoCamera.ts`) DERIVES its
 * pitch from this exact number and nothing else, which is why there is no
 * separate degrees constant — a second, independently authored camera angle
 * would let the 2D and 3D renderers drift apart (3D characters would stop
 * standing on the shadows/bars/hit-tests the 2D renderer draws for them).
 * Editing this number MOVES THE CAMERA; it does not merely squash the map.
 * `isoCamera.test.ts` asserts `sin(ISO_ELEVATION) === ISO_Y_COMPRESSION`, so
 * the two can never silently disagree (#842).
 *
 * `0.650647 = sin(40.59°)` — the shipped camera elevation.
 *
 * History: `0.5` would be a classic 2:1 iso; #432 raised it to `0.75`
 * (`asin(0.75) ≈ 48.59°`) from #338's investigation: the original 0.5 squash
 * was a perception artifact that made screen-X-dominant motion read as faster
 * than screen-Y-dominant motion even though the sim moves every direction at
 * the same true world speed (see #270/#273, which fixed the sim side and are
 * untouched by this constant). #842 brought the view 8° further down —
 * `48.59° − 8° = 40.59°`, `sin(40.59°) = 0.650647` — for a more side-on read.
 * That trade is deliberate and was accepted with eyes open: the map projects
 * 13.25% SHORTER top-to-bottom (`ISO_SCALE_Y` falls with `k`) and heights
 * project 14.8% TALLER (`cos EL` rises), which is the "more side-on" look. The
 * fairness zoom target {@link TARGET_WORLD_H} is in WORLD units and was NOT
 * retuned; the solved zoom simply rises ~15% to keep the same world height in
 * frame. Lowering the camera further re-approaches #338's anisotropy — see
 * below — so treat `k` as a bounded dial, not a free one.
 *
 * #730 (axis-aligning the basis) does NOT reintroduce #338: the apparent-speed
 * anisotropy is the ratio between the fastest and slowest screen speed for a
 * fixed world speed, and that ratio is EXACTLY `1/k` under both bases — the
 * old rotated basis made a unit world-speed step project to `√2·ISO_SCALE_X`
 * along the screen-x diagonal vs `√2·ISO_SCALE_Y` along the screen-y diagonal;
 * the new one makes it `ISO_SCALE_X` along +x vs `ISO_SCALE_Y` along +y. Same
 * `1/k` (`1.537` at #842's `k`, was `1.333` at #432's); the fast/slow axes just
 * stop being diagonal. Rotating the basis changes WHICH world directions look
 * fast, never HOW MUCH faster.
 */
export const ISO_Y_COMPRESSION = 0.650647;

/** Screen pixels per world unit along the screen Y (down) axis, at viewport
 *  zoom 1. `ISO_SCALE_X * ISO_Y_COMPRESSION` (#432 — was `ISO_SCALE_X / 2`,
 *  i.e. `ISO_Y_COMPRESSION = 0.5`; #842 moved `k` to `sin(40.59°)`). */
export const ISO_SCALE_Y = ISO_SCALE_X * ISO_Y_COMPRESSION;

/** Screen magnification applied to projected body radii by the
 *  placeholder renderer AND the hit-test. Must stay shared so the
 *  clickable area always matches the drawn area.
 *
 *  #277: was `3`, which drew (and hit-tested) the procedural fallback
 *  circle at 3× a champion's true collision radius — visibly oversized
 *  vs. what the sim actually collides/targets against. `1` draws the
 *  circle (and sizes the hit-test/pillar footprint that share this
 *  constant) at the real collision radius; the `Math.max(3, …)` px floor
 *  at the call sites keeps it from vanishing at extreme zoom-out. */
export const BODY_MAGNIFICATION = 1;

/** Reference body radius (world units) for a champion — matches content
 *  warrior.radius (50). Lives here (not in `entityView`) so both the 2D body
 *  draw and the 3D character scale (#237) derive from the SAME constant without
 *  the consumer pulling in Pixi. Static rather than looked up so the projection
 *  module stays content-decoupled; if bodies gain per-champion radii the shape
 *  becomes `radius by entityId → look up from the content bundle passed in`. */
export const DEFAULT_BODY_RADIUS_UNITS = 50;

/** Historical body-magnification factor, frozen at its pre-#277 value of
 *  `3` and used ONLY to derive {@link CHARACTER_TARGET_HEIGHT_UNITS} below.
 *  #277 shrank the LIVE `BODY_MAGNIFICATION` (2D procedural circle +
 *  hit-test + pillar footprint) down to the true collision radius; the
 *  already-correctly-sized 3D model's target height must NOT move with
 *  that change, so its derivation keeps this decoupled, never-changing
 *  multiplier instead of referencing `BODY_MAGNIFICATION` directly. */
const LEGACY_HEIGHT_MAGNIFICATION = 3;

/**
 * Visual scale applied to EVERY champion's 3D model height, relative to the
 * legacy body-derived size (#858). `1.0` reproduces the pre-#858 rendering
 * exactly (300 units = 3× the collision diameter); `0.6` is the shipped value,
 * putting models at 180 units = 1.8× the collision diameter.
 *
 * THE knob for "models look too big / too small" — adjust this value and
 * nothing else, NEVER a per-model or per-champion override. `characterModel.ts`
 * normalises every champion to one target height, and `entityView.ts`'s
 * {@link CHARACTER_TARGET_HEIGHT_UNITS}-derived bar-stack base depends on that
 * staying a single static number rather than a per-instance measurement. If
 * model size is ever exposed as a user setting, that setting reads THIS
 * constant as its default instead of introducing a parallel one.
 *
 * Why the pre-#858 3× existed at all: {@link LEGACY_HEIGHT_MAGNIFICATION} was
 * frozen at its pre-#277 value when the LIVE 2D body sizing shrank to the true
 * collision radius, so the 3× was a historical accident rather than an art
 * choice. #825's ground marker draws the entity's real (aura-modified) body
 * radius on the floor, which made the mismatch directly visible.
 */
export const CHARACTER_MODEL_SCALE = 0.6;

/**
 * Target on-floor height (world units) for a champion's 3D model (#237) —
 * derived from the collision body, NEVER a hand-tuned per-model number. The
 * 3D model is given visual mass matching the (pre-#277) magnified 2D body
 * diameter — see {@link LEGACY_HEIGHT_MAGNIFICATION} for why this no longer
 * reads live `BODY_MAGNIFICATION` — scaled by the single art knob
 * {@link CHARACTER_MODEL_SCALE} (#858). Lives here (its canonical home — all
 * inputs are projection constants) rather than in
 * `three3d/characterModel.ts`, which re-exports it, so 2D-only consumers (the
 * HP bar/nameplate head-height offset, #246) can read it without pulling in
 * three.js.
 */
export const CHARACTER_TARGET_HEIGHT_UNITS =
  2 * DEFAULT_BODY_RADIUS_UNITS * LEGACY_HEIGHT_MAGNIFICATION * CHARACTER_MODEL_SCALE;

/** Number of facing sectors. Kept as a local constant so the projection
 *  module has zero runtime dependencies on @pit/sim; the sim owns the
 *  authoritative value (`FACING_SECTORS = 16`) and the lint rule allows
 *  type-only imports from it. If the sim bumps this, we bump here.
 *  A single test asserts equality against the sim's runtime constant. */
export const FACING_SECTORS = 16 as const;

/**
 * Distinct float type for world-space coordinates in the render layer.
 * Interpolation produces these; `worldToScreen` accepts either flavour
 * (branded Units OR RenderWorldPos.wx/wy). This is what keeps
 * interpolation's floats OUT of protocol types — `RenderWorldPos` is
 * structurally incompatible with `{ x: Units, y: Units }` because of
 * the field names.
 */
export interface RenderWorldPos {
  readonly wx: number;
  readonly wy: number;
}

/** Screen-space point (floats — presentation layer). */
export interface ScreenPos {
  readonly sx: number;
  readonly sy: number;
}

/**
 * The viewport transform: a uniform `zoom` about the world origin followed by
 * a `(cx, cy)` screen-space translation. Recomputed every frame from the
 * canvas size, the map size and the followed player (`camera.ts`).
 *
 * `zoom` (#730) multiplies BOTH scales, so it never changes the projected
 * shape — only how many world units fit on screen. It exists because
 * "playable area fills the whole viewport" is a relation between the
 * projected MAP RECT and the CANVAS RECT: an axis-aligned basis makes the two
 * comparable, and the cover zoom is what makes the map rect big enough (see
 * {@link viewportCoverZoom}).
 */
export interface ProjectionCenter {
  readonly cx: number;
  readonly cy: number;
  /** Uniform magnification over the base `ISO_SCALE_*`. `1` = base scale. */
  readonly zoom: number;
}

/** The IDENTITY transform (no translation, base scale). Also the coordinate
 *  space the Pixi world layers are drawn in — the live camera is applied by
 *  scaling/positioning those containers, so draw code passes this and only
 *  true screen-space consumers pass the live centre (see the module header). */
export const NO_OFFSET: ProjectionCenter = { cx: 0, cy: 0, zoom: 1 };

/** A pure-zoom transform (no translation) — the container space a world layer
 *  scaled by `zoom` draws in. */
export function zoomOnly(zoom: number): ProjectionCenter {
  return { cx: 0, cy: 0, zoom };
}

/**
 * Rasterisation resolution for world-anchored Pixi `Text` (nameplates, combat
 * text, ability labels), in texture px per layout px.
 *
 * These live INSIDE the world layers, so the viewport cover zoom (#730)
 * magnifies them along with everything else — which is what we want
 * proportionally, but a glyph rasterised at 1× and then scaled up 2–3× is
 * visibly soft. Rasterising at this fixed supersample keeps text crisp for any
 * cover zoom up to `3×` without plumbing the live zoom into every text
 * creation site (the zoom only changes on resize, but a `Text` re-rasterises
 * whenever `resolution` changes — not something to do mid-match).
 *
 * A flat number rather than `devicePixelRatio × zoom` because this module is
 * pure (no DOM) and because desktop DPR is 1–2: 3 covers DPR 1 at zoom 3 and
 * DPR 2 at zoom 1.5, at the cost of a slightly larger glyph atlas.
 */
export const WORLD_TEXT_RESOLUTION = 3;

/** On-screen size, in px, of the whole map rectangle at a given zoom.
 *  Under the axis-aligned basis (#730) the projected map IS a rectangle
 *  spanning `[0, mapW·ISO_SCALE_X·zoom] × [0, mapH·ISO_SCALE_Y·zoom]` from
 *  the projected world origin — no diamond bounding box, no negative extent. */
export function projectedMapSize(
  mapW: number,
  mapH: number,
  zoom: number,
): { readonly w: number; readonly h: number } {
  return { w: mapW * ISO_SCALE_X * zoom, h: mapH * ISO_SCALE_Y * zoom };
}

/**
 * The VERTICAL world extent (world units) the follow camera shows at every
 * resolution — THE framing knob (#782), and the one number to turn if the view
 * feels too close or too far. {@link DEFAULT_ZOOM} is not that knob (see its
 * doc): it is base scale, and it cancels out of the target zoom below.
 *
 * WHY a target extent rather than a target zoom: #778's cover zoom sat exactly
 * on the zero-black floor (`max(canvasW/mapPxW, arenaH/mapPxH)`), which makes
 * the VIEWPORT decide framing — a 3440×1440 display saw 100% of `landscape`'s
 * width while 1280×720 saw 75%. That is an information asymmetry (screen size
 * as a competitive advantage), and on wide displays it showed the WHOLE map,
 * destroying partial map awareness. Pinning the vertical extent instead makes a
 * bigger screen buy bigger PIXELS, not more intel (measured on `landscape`:
 * champion radius 21px @1280×720 → 69px @3840×2160, vertical extent 82% of the
 * map at both).
 *
 * WHY the vertical axis: two different aspect ratios cannot show an identical
 * world rectangle without bars — geometry forces a choice of the "fair" axis.
 * Vertical is the top-down convention (LoL/Dota): an ultrawide's extra width
 * reads as peripheral vision rather than extra information. The width floor
 * only starts binding past ~3:1 (wider than 21:9; it was ~2.6:1 before #842
 * lowered `k` — a smaller `k` raises `zTarget`, so the width term takes even
 * longer to catch it), and even there the vertical extent still does not reach
 * 100% of the map.
 *
 * `1900` was picked on-screen against `landscape` (4096×2304 → 82% of its
 * height). Measured alternatives: `1700` → 74%, `2100` → 91%. A pure taste
 * call: changing it is a one-number edit with no sim/wire/hash implication.
 * A map SHORTER than this (or a degenerate one) simply falls back to the
 * zero-black floor — see {@link viewportFollowZoom}.
 */
export const TARGET_WORLD_H = 1900;

/**
 * The smallest zoom (>= 1) at which the projected map rectangle COVERS a
 * `viewportW × viewportH` rectangle on both axes — the other half of #730's
 * "zero black, ever" requirement.
 *
 * Axis-aligning the basis makes the map edge parallel to the screen edge, so a
 * camera clamped inside the map shows only playable area — but only while the
 * projected map is at least viewport-sized. At the base zoom a 4096×2304 map
 * projects to ~1016×428 px, far smaller than any real window, so the clamp
 * would center it and surround it with black. Covering is therefore not an
 * alternative to the basis change (a diamond can't cover a rectangle at ANY
 * zoom — which is why #730 rules zoom OUT as a standalone fix); it is what the
 * rectangular basis makes possible for the first time.
 *
 * The VIEWPORT here is not always the whole canvas: since #781 the follow camera
 * passes the ARENA VIEWPORT (`canvasH` minus the reserved bottom HUD strip — see
 * `hudLayout.ts`), because that is the rectangle the zero-black invariant is
 * defined over. Static "arenaCenter" framing still passes the full canvas.
 *
 * Since #782 this is a FLOOR, not the whole policy: zero black holds at ANY
 * zoom at or above it (the clamp in `computeCameraCenter` keeps an over-sized
 * map covering the viewport; black can only appear when the projected map is
 * SMALLER than the viewport, where there is nothing left to clamp against). The
 * follow camera therefore takes `max(this, zTarget)` — see
 * {@link viewportFollowZoom}. This function itself is unchanged and remains the
 * WHOLE policy for the static "arenaCenter" mode (see
 * {@link computeArenaCenter}).
 *
 * Floored at 1 so this only ever zooms IN. A degenerate (zero-size) map or
 * viewport yields 1 rather than 0/∞.
 */
export function viewportCoverZoom(
  viewportW: number,
  viewportH: number,
  mapW: number,
  mapH: number,
): number {
  const base = projectedMapSize(mapW, mapH, 1);
  const needW = base.w > 0 ? viewportW / base.w : 1;
  const needH = base.h > 0 ? viewportH / base.h : 1;
  return Math.max(1, needW, needH);
}

/**
 * The FOLLOW CAMERA's zoom (#782) — a BOUNDED policy: equal vertical world
 * extent at every resolution, never the whole map, still zero black.
 *
 *     zTarget = viewportH / (ISO_SCALE_Y · TARGET_WORLD_H)   (fairness)
 *     zFloor  = viewportCoverZoom(...)                       (zero black)
 *     zoom    = max(zFloor, zTarget)
 *
 * `zTarget` is the zoom at which exactly {@link TARGET_WORLD_H} world units of
 * HEIGHT fit in the viewport — solve `viewportH = worldH · ISO_SCALE_Y · zoom`
 * for `zoom`. Note {@link DEFAULT_ZOOM} cancels: `zTarget · ISO_SCALE_Y` is
 * `viewportH / TARGET_WORLD_H`, independent of it.
 *
 * `zFloor` is #778's cover zoom, now used as what it always was — a floor.
 * Taking the max satisfies both properties with no letterboxing and no change
 * to the camera clamp: raising zoom ABOVE the floor only makes covering easier
 * (the projected map grows strictly past the viewport on both axes).
 *
 * WHICH TERM BINDS: `zTarget` at every ordinary aspect ratio, so vertical
 * extent is EXACTLY equal across resolutions and a bigger screen buys bigger
 * pixels rather than more visible world. `zFloor`'s WIDTH term takes over past
 * ~3:1 (wider than 21:9; ~2.6:1 before #842 — see {@link TARGET_WORLD_H}),
 * where width reaches 100% of the map but height
 * still does not. `zFloor`'s HEIGHT term binds only when
 * `TARGET_WORLD_H >= mapH` (a map shorter than the target extent) or for
 * degenerate inputs — both fall back to pure cover framing cleanly.
 *
 * MODE SPLIT (#782, deliberate): only the follow camera adopts this. The static
 * {@link computeArenaCenter} — demo harness / visual baseline — stays PURE FIT
 * on {@link viewportCoverZoom} so whole-scene screenshots stay byte-stable.
 *
 * Degenerate inputs (zero-size viewport or map) yield the cover zoom's `1`
 * rather than `0`/`Infinity`/`NaN`: with no viewport height there is no
 * vertical extent to equalise.
 */
export function viewportFollowZoom(
  viewportW: number,
  viewportH: number,
  mapW: number,
  mapH: number,
): number {
  const floorZoom = viewportCoverZoom(viewportW, viewportH, mapW, mapH);
  const base = projectedMapSize(mapW, mapH, 1);
  if (!(viewportH > 0) || base.h <= 0) return floorZoom;
  const targetZoom = viewportH / (ISO_SCALE_Y * TARGET_WORLD_H);
  return Math.max(floorZoom, targetZoom);
}

/**
 * Static arena framing: cover-zoom the map and center the (over-sized) map
 * rectangle in a `viewportW × viewportH` rectangle. Used by the demo harness /
 * visual baseline camera mode, which passes the FULL canvas (it has no live
 * action bar).
 *
 * PURE FIT, by decision (#782): this mode keeps {@link viewportCoverZoom} as
 * its whole zoom policy — it sits on the zero-black floor and shows as much of
 * the map as the viewport allows, so whole-scene screenshots stay byte-stable.
 * Only the follow camera adopts the bounded {@link viewportFollowZoom} policy
 * (including its "no player yet" fallback, which statically centers at the SAME
 * zoom the live camera will use, so the framing does not jump when the first
 * snapshot lands — see `camera.ts`).
 *
 * Under the axis-aligned basis (#730) the projected extent is just the scaled
 * map rectangle — `[0, mapW·SX·zoom] × [0, mapH·SY·zoom]` — not the old
 * `(mapW + mapH)` diamond bounding box.
 */
export function computeArenaCenter(
  viewportW: number,
  viewportH: number,
  mapW: number,
  mapH: number,
): ProjectionCenter {
  const zoom = viewportCoverZoom(viewportW, viewportH, mapW, mapH);
  const { w, h } = projectedMapSize(mapW, mapH, zoom);
  return { cx: (viewportW - w) / 2, cy: (viewportH - h) / 2, zoom };
}

/**
 * World → screen. Accepts branded integer `Units` OR unbranded floats
 * (via the RenderWorldPos overload); interpolation feeds the latter.
 *
 * NORMATIVE — the ONE forward conversion. Do not duplicate this math
 * anywhere else in the client.
 */
export function worldToScreen(
  x: Units | number,
  y: Units | number,
  center: ProjectionCenter = NO_OFFSET,
): ScreenPos {
  // `Units` is `number & { __brand }` — arithmetic yields plain `number`
  // and the brand is silently dropped. This is one of the two documented
  // brand-escape sites in the client render layer.
  return {
    sx: x * ISO_SCALE_X * center.zoom + center.cx,
    sy: y * ISO_SCALE_Y * center.zoom + center.cy,
  };
}

/**
 * Convenience: project a `RenderWorldPos` (float interpolation output).
 */
export function renderWorldToScreen(
  p: RenderWorldPos,
  center: ProjectionCenter = NO_OFFSET,
): ScreenPos {
  return worldToScreen(p.wx, p.wy, center);
}

/**
 * Screen → world (inverse). Rounds to the nearest integer world unit
 * (so callers can safely narrow the result to `Units` if they need to
 * hand it back into sim types — WP6 itself never does this).
 *
 * Clamping to map bounds is the caller's job; the projection is a pure
 * bijection modulo the rounding.
 */
export function screenToWorld(
  sx: number,
  sy: number,
  center: ProjectionCenter = NO_OFFSET,
): { readonly x: number; readonly y: number } {
  // Exact inverse of `worldToScreen` (#730 — the axis-aligned basis makes the
  // 2×2 system diagonal, so each axis inverts independently):
  //   sx = x·ISO_SCALE_X·zoom + cx  ⇒  x = (sx − cx) / (ISO_SCALE_X·zoom)
  //   sy = y·ISO_SCALE_Y·zoom + cy  ⇒  y = (sy − cy) / (ISO_SCALE_Y·zoom)
  // This is the click-to-target / aim path: if the inverse is not exact,
  // targeting silently drifts (a round-trip property test pins it).
  const rawX = (sx - center.cx) / (ISO_SCALE_X * center.zoom);
  const rawY = (sy - center.cy) / (ISO_SCALE_Y * center.zoom);
  // `|| 0` normalizes a `-0` rounding result (float noise landing a hair below
  // an exact integer) to `+0` — same numeric value, but keeps this a clean
  // canonical bijection rather than leaking sign-of-zero into callers.
  return { x: Math.round(rawX) || 0, y: Math.round(rawY) || 0 };
}

/**
 * Project a world-space radius (in units) to a screen-space radius (in
 * pixels), at the given viewport `zoom` (default 1 = container space, which
 * is what every in-container draw call wants — see the module header).
 *
 * A world collision circle of radius `r` projects, under the axis-aligned
 * basis (#730), to an axis-aligned ELLIPSE with half-width `r·ISO_SCALE_X` and
 * half-height `r·ISO_SCALE_Y` — exactly, with no rotation. We report the
 * half-WIDTH: it is the collision-registered horizontal extent every consumer
 * shares (drawn body, pillar footprint, hit-test). Multiply by
 * `ISO_SCALE_Y / ISO_SCALE_X` to recover the half-height of a ground ellipse
 * (`groundMarker.ts` does exactly that; `arena.ts` used to expose it as
 * `ISO_FOOTPRINT_SQUASH` for the 2D pillar base, retired in #829).
 *
 * (Under the old rotated basis the true projected ellipse was `√2` LARGER than
 * this on both axes, so the drawn body was `√2` smaller than the collision
 * circle it claimed to register. The axis-aligned basis makes the two exactly
 * equal — bible §7's "collision circle = visual footprint" is now literal.)
 */
export function projectRadius(radiusUnits: Units | number, zoom = 1): number {
  return radiusUnits * ISO_SCALE_X * zoom;
}

/**
 * Screen pixels per world-space VERTICAL unit (height above the ground
 * plane, e.g. a 3D character's standing height) as seen through the iso
 * camera (`three3d/isoCamera.ts`) — the pixel factor a "how tall is this
 * on screen" computation (#246, the HP-bar/nameplate head-height offset)
 * must use, since a height axis does NOT project 1:1 with `projectRadius`
 * (a GROUND-plane displacement).
 *
 * DERIVATION (kept in this pure, three.js-free module so 2D-only consumers
 * never need to import the 3D camera to get this number — cross-checked
 * against the actual `THREE.OrthographicCamera` in `isoCamera.test.ts`):
 * since #730 the camera has YAW = 0 (screen-right IS world +x) and
 * ELEVATION = asin(ISO_SCALE_Y / ISO_SCALE_X) = asin(k), at
 * `ISO_PX_PER_UNIT = ISO_SCALE_X / cos(YAW) = ISO_SCALE_X` screen px per
 * world unit (both screen axes, since the ortho frustum is scaled identically
 * in x/y). A pure vertical (world-height) displacement is horizontally
 * invariant on screen and moves along screen-up by a fraction
 * `cos(ELEVATION)` of that per-unit scale:
 *
 *   pxPerUnit = ISO_PX_PER_UNIT * cos(ELEVATION)
 *             = ISO_SCALE_X * sqrt(1 - (ISO_SCALE_Y/ISO_SCALE_X)²)
 *             = sqrt(ISO_SCALE_X² - ISO_SCALE_Y²)
 *
 * (Pre-#730 the yaw was 45°, whose `1/cos(45°) = √2` inflated this by √2 —
 * the rotated basis stretched every ground displacement by the same √2, see
 * `projectRadius`. Dropping the yaw drops the √2 from both, together.)
 */
export const ISO_VERTICAL_PX_PER_UNIT = Math.sqrt(
  ISO_SCALE_X * ISO_SCALE_X - ISO_SCALE_Y * ISO_SCALE_Y,
);

/**
 * Project a world-space VERTICAL extent (e.g. a 3D character's standing
 * height, ground to head) to a screen-space pixel span, at the given viewport
 * `zoom` (default 1 = container space, as for `projectRadius`). Unlike
 * `projectRadius` (a ground-plane radius), a height runs along the camera's
 * up-axis, which is foreshortened by `cos(ELEVATION)` — see
 * `ISO_VERTICAL_PX_PER_UNIT` for the derivation.
 */
export function projectHeight(heightUnits: number, zoom = 1): number {
  return heightUnits * ISO_VERTICAL_PX_PER_UNIT * zoom;
}

/**
 * Precomputed unit direction vectors in SCREEN SPACE for every facing
 * sector. The sim's `SECTOR_UNIT_DELTAS` are integer world-space
 * deltas; we project them through the transform so the facing tick lands
 * where the eye expects. Under the axis-aligned basis (#730) sector 0 (world
 * N) points straight up the screen and sector 4 (world E) straight right —
 * pre-#730 the 45° basis rotation put N up-and-right.
 *
 * NOTE — floats are legal here (presentation). The magnitudes are
 * hand-computed from the sim's canonical integer deltas
 * (`SECTOR_UNIT_DELTAS[k] / 10000` scaled to unit length, then run
 * through the projection).
 *
 * Keeping this table STATIC (not derived at import) keeps the render
 * layer's runtime-import surface on @pit/sim to zero.
 */
function precomputeFacingScreenVectors(): readonly {
  readonly sdx: number;
  readonly sdy: number;
}[] {
  // Sim SECTOR_UNIT_DELTAS scaled to unit length (÷10000). Angles are
  // 22.5°-spaced clockwise from N; screen-Y grows downward.
  const world: readonly { dx: number; dy: number }[] = [
    { dx: 0, dy: -1 }, //     0 N
    { dx: 0.3827, dy: -0.9239 }, //  1 NNE
    { dx: 0.7071, dy: -0.7071 }, //  2 NE
    { dx: 0.9239, dy: -0.3827 }, //  3 ENE
    { dx: 1, dy: 0 }, //     4 E
    { dx: 0.9239, dy: 0.3827 }, //   5 ESE
    { dx: 0.7071, dy: 0.7071 }, //   6 SE
    { dx: 0.3827, dy: 0.9239 }, //   7 SSE
    { dx: 0, dy: 1 }, //      8 S
    { dx: -0.3827, dy: 0.9239 }, //  9 SSW
    { dx: -0.7071, dy: 0.7071 }, // 10 SW
    { dx: -0.9239, dy: 0.3827 }, // 11 WSW
    { dx: -1, dy: 0 }, //    12 W
    { dx: -0.9239, dy: -0.3827 }, // 13 WNW
    { dx: -0.7071, dy: -0.7071 }, // 14 NW
    { dx: -0.3827, dy: -0.9239 }, // 15 NNW
  ];
  // All 16 input directions are non-zero — mag > 0 is guaranteed by
  // construction. Kept without a defensive branch so coverage stays
  // total (a runtime test asserts every entry is unit-length).
  return world.map(({ dx, dy }) => {
    // Project the direction vector, then RENORMALIZE to unit length
    // so the tick indicator has consistent visual length across the
    // squashed iso view.
    const sdxRaw = dx * ISO_SCALE_X;
    const sdyRaw = dy * ISO_SCALE_Y;
    const mag = Math.hypot(sdxRaw, sdyRaw);
    return { sdx: sdxRaw / mag, sdy: sdyRaw / mag };
  });
}

/**
 * Unit screen-space direction vector for a facing sector, in the same
 * order as the sim's `SECTOR_UNIT_DELTAS` (index = sector). Length 1
 * so callers scale by the desired tick length in pixels.
 */
export const FACING_SCREEN_VECTORS: readonly {
  readonly sdx: number;
  readonly sdy: number;
}[] = precomputeFacingScreenVectors();

/**
 * Look up the screen-space unit direction for a facing sector index
 * (0..15 at 16-way facing). Accepts a plain `number` — the wire type
 * `Facing` is a number, and this module is intentionally decoupled from
 * the sim's stricter `Facing` union so it stays type-import-free.
 *
 * Total coverage: every valid sector (test walks all 16). Out-of-range
 * inputs throw defensively.
 */
export function facingToScreenVec(
  f: number,
): { readonly sdx: number; readonly sdy: number } {
  const v = FACING_SCREEN_VECTORS[f];
  // Defensive — valid sectors are 0..15 and the table has 16 entries;
  // TS narrowing via `noUncheckedIndexedAccess` requires the guard.
  if (v === undefined) {
    throw new Error(`facingToScreenVec: invalid facing sector ${String(f)}`);
  }
  return v;
}
